import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@contexts/AuthContext';
import { loadConfig } from './config';

// Components
import { Login } from '@components/Login';
import { AuthCallback } from '@components/AuthCallback';
import { ProtectedRoute } from '@components/ProtectedRoute';
import { DashboardLayout } from '@components/DashboardLayout';

// Pages (serão criadas nos próximos passos)
import { DashboardView } from '@pages/dashboard/DashboardView';
import { ProductsPage, ProductFormView, ProductDetailView } from '@pages/products';
import { ProviderListView, ProviderFormView, ProviderDetailView } from '@pages/providers';
import { CompositeProductListView, CompositeProductFormView } from '@pages/compositeProducts';
import { RawMaterialListView, RawMaterialFormView, RawMaterialDetailView } from '@pages/rawMaterials';
import { PurchaseOrderListView, PurchaseOrderFormView, PurchaseOrderDetailView } from '@pages/purchaseOrders';
import { MaterialReceiptListView, MaterialReceiptFormView, MaterialReceiptDetailView } from '@pages/materialReceipts';
import { StockMovementsPageView } from '@pages/stockMovements';
import {
  ActivityListView,
  ActivityFormView,
  EmployeeListView,
  EmployeeFormView,
  BomListView,
  BomFormView,
  PendingManufacturingView,
  ActivityLogListView,
  ProductionOrderListView,
  ProductionOrderFormView,
  ProductionOrderDetailView,
  ProductionReportsView,
} from '@pages/production';
import { InventorySessionDetailView } from '@pages/inventorySessions';
import { SalesOrderListView, SalesOrderDetailView, SalesOrderFormView } from '@pages/salesOrders';
import { DemoReconciliationListView, DemoReconciliationWorkspaceView } from '@pages/demoReconciliation';
import { InvoiceListView, InvoiceDetailView, InvoiceFormView } from '@pages/invoices';
import { InvoiceTransactionTypeListView, InvoiceTransactionTypeFormView } from '@pages/invoiceTransactionTypes';
import { HolidayListView, HolidayFormView } from '@pages/holidays';
import { CompanyListView, CompanyFormView } from '@pages/companies';
import {
  InvoiceInvalidationListView,
  InvoiceInvalidationFormView,
  InvoiceInvalidationDetailView,
} from '@pages/invoiceInvalidations';
import {
  SefazCommunicationLogListView,
  SefazCommunicationLogDetailView,
} from '@pages/sefazCommunicationLogs';
import { ProcessExecutionView } from '@pages/processes';
import { SupplyInvoiceListView, SupplyInvoiceFormView } from '@pages/supplyInvoices';
import { ServiceInvoiceListView } from '@pages/serviceInvoices';
import { SalesReportView } from '@pages/salesReport';
import { StockReportsView } from '@pages/stockMovementReport';
import { AccountingReportsView } from '@pages/accountingReports';
import { FinancialReportsView } from '@pages/financialReports';
import { ShippingReportsPage } from '@pages/shippingReport';
import { PurchaseReportView } from '@pages/purchaseReport';
import { GoodsPickupOrderListView, GoodsPickupOrderFormView, GoodsPickupOrderDetailView } from '@pages/goodsPickupOrders';
import { GoodsDeliveryOrderListView, GoodsDeliveryOrderFormView, GoodsDeliveryOrderDetailView } from '@pages/goodsDeliveryOrders';
import { DriverListView, DriverFormView } from '@pages/drivers';
import { VehicleListView, VehicleFormView } from '@pages/vehicles';
import { FleetMapView, TrackersView } from '@pages/fleetTracking';
import { UserGuidePage } from '@pages/userGuide';
import { ShippingProviderListView } from '@pages/shippingProviders';
import { InvoiceShippingOrderListView } from '@pages/invoiceShippingOrders';
import { DriverRouteListView, DriverRouteFormView, DriverRouteDetailView, DeliveryExecutionListView, DeliveryExecutionDetailView } from '@pages/driverRoutes';
import { EventListView, EventFormView, InvitationListView, InvitationFormView, InvitationDetailView, RoutePlanningView } from '@pages/events';
import { OrderTypeListView, OrderTypeFormView } from '@pages/orderTypes';
import { OrderStatusListView, OrderStatusFormView } from '@pages/orderStatuses';
import { SalesChannelListView, SalesChannelFormView } from '@pages/salesChannels';
import { VariableFeesView } from '@pages/variableFees';
import { SalesGoalsView } from '@pages/salesGoals';
import { ReceivingHubView } from '@pages/receivingHub';
import { CheckingAccountListView, CheckingAccountFormView } from '@pages/checkingAccounts';
import { CreditCardListView, CreditCardFormView } from '@pages/creditCardSettings';
import {
  FinancialTransactionsView,
  CreditCardsView,
  RecurringTransactionsView,
  TransactionImportView,
  ImportCorrelationsView,
  AccountingPlanView,
  ProductEarnView,
} from '@pages/financial';

// Componente de Loading inicial
const AppLoading: React.FC = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl flex items-center justify-center mx-auto mb-4">
        <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
      </div>
      <h2 className="text-xl font-semibold text-slate-700 mb-2">
        Carregando ERP Viver Organizer
      </h2>
      <p className="text-slate-500">
        Preparando a aplicação...
      </p>
    </div>
  </div>
);

// Componente de erro
const AppError: React.FC<{ error: string }> = ({ error }) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="text-center max-w-md">
      <div className="text-6xl text-red-400 mb-4">⚠️</div>
      <h2 className="text-xl font-semibold text-slate-700 mb-2">
        Erro ao carregar aplicação
      </h2>
      <p className="text-slate-500 mb-4">
        {error}
      </p>
      <button 
        onClick={() => window.location.reload()}
        className="btn-primary"
      >
        Tentar Novamente
      </button>
    </div>
  </div>
);

// Redirect de rotas legacy
const LegacyRedirect: React.FC = () => {
  const { user } = useAuth();
  
  // Se autenticado, redirecionar para dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Se não autenticado, redirecionar para login
  return <Navigate to="/login" replace />;
};

// Main App Component
const AppContent: React.FC = () => {
  console.log('🧩 APPCONTENT: Component renderizando...');
  
  return (
    <Router>
      <Routes>
        {/* Rota pública de login */}
        <Route path="/login" element={<Login />} />
        
        {/* Rota de callback do Azure AD */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        
        {/* Rotas protegidas com layout */}
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          {/* Página principal */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          
          {/* Dashboard */}
          <Route path="dashboard" element={<DashboardView />} />
          
          {/* Produtos */}
          <Route path="products" element={<ProductsPage />} />
          <Route path="products/new" element={<ProductFormView />} />
          <Route path="products/:id" element={<ProductDetailView />} />
          <Route path="products/:id/edit" element={<ProductFormView />} />
          
          {/* Fornecedores */}
          <Route path="providers" element={<ProviderListView />} />
          <Route path="providers/new" element={<ProviderFormView />} />
          <Route path="providers/:id" element={<ProviderDetailView />} />
          <Route path="providers/:id/edit" element={<ProviderFormView />} />

          {/* Produtos Compostos */}
          <Route path="composite-products" element={<CompositeProductListView />} />
          <Route path="composite-products/new" element={<CompositeProductFormView />} />

          {/* Matérias-Primas */}
          <Route path="raw-materials" element={<RawMaterialListView />} />
          <Route path="raw-materials/new" element={<RawMaterialFormView />} />
          <Route path="raw-materials/:id" element={<RawMaterialDetailView />} />
          <Route path="raw-materials/:id/edit" element={<RawMaterialFormView />} />

          {/* Pedidos de Compra */}
          <Route path="purchase-orders" element={<PurchaseOrderListView />} />
          <Route path="purchase-orders/new" element={<PurchaseOrderFormView />} />
          <Route path="purchase-orders/:id" element={<PurchaseOrderDetailView />} />
          <Route path="purchase-orders/:id/edit" element={<PurchaseOrderFormView />} />

          {/* Recebimento de Materiais */}
          <Route path="material-receipts" element={<MaterialReceiptListView />} />
          <Route path="material-receipts/new" element={<MaterialReceiptFormView />} />
          <Route path="material-receipts/:id" element={<MaterialReceiptDetailView />} />
          <Route path="material-receipts/:id/edit" element={<MaterialReceiptFormView />} />
          <Route path="stock-movements" element={<StockMovementsPageView />} />
          <Route path="stock-movements/inventory/:id" element={<InventorySessionDetailView />} />

          {/* Produção */}
          <Route path="production/pending" element={<PendingManufacturingView />} />
          <Route path="production/activities" element={<ActivityListView />} />
          <Route path="production/activities/new" element={<ActivityFormView />} />
          <Route path="production/activities/:id/edit" element={<ActivityFormView />} />
          <Route path="production/employees" element={<EmployeeListView />} />
          <Route path="production/employees/new" element={<EmployeeFormView />} />
          <Route path="production/employees/:id/edit" element={<EmployeeFormView />} />
          <Route path="production/bom" element={<BomListView />} />
          <Route path="production/bom/new" element={<BomFormView />} />
          <Route path="production/bom/:id/edit" element={<BomFormView />} />
          <Route path="production/activity-logs" element={<ActivityLogListView />} />
          <Route path="production/orders" element={<ProductionOrderListView />} />
          <Route path="production/orders/new" element={<ProductionOrderFormView />} />
          <Route path="production/orders/:id" element={<ProductionOrderDetailView />} />
          <Route path="production/orders/:id/edit" element={<ProductionOrderFormView />} />
          <Route path="production/reports" element={<ProductionReportsView />} />

          {/* Pedidos de Venda */}
          <Route path="sales-orders" element={<SalesOrderListView />} />
          <Route path="sales-orders/new" element={<SalesOrderFormView />} />
          <Route path="sales-orders/:id" element={<SalesOrderDetailView />} />

          {/* Conciliação de Demonstração */}
          <Route path="demo-reconciliation" element={<DemoReconciliationListView />} />
          <Route path="demo-reconciliation/:customerId" element={<DemoReconciliationWorkspaceView />} />

          {/* Notas Fiscais */}
          <Route path="invoices" element={<InvoiceListView />} />
          <Route path="invoices/new" element={<InvoiceFormView />} />
          <Route path="invoices/:id" element={<InvoiceDetailView />} />

          {/* Notas Fiscais de Serviço — Vendas */}
          <Route path="service-invoices" element={<ServiceInvoiceListView />} />

          {/* Notas Fiscais — Suprimentos */}
          <Route path="supply-invoices" element={<SupplyInvoiceListView />} />
          <Route path="supply-invoices/new" element={<SupplyInvoiceFormView />} />

          {/* Ordens de Retirada */}
          <Route path="goods-pickup-orders" element={<GoodsPickupOrderListView />} />
          <Route path="goods-pickup-orders/new" element={<GoodsPickupOrderFormView />} />
          <Route path="goods-pickup-orders/:id" element={<GoodsPickupOrderDetailView />} />
          <Route path="goods-pickup-orders/:id/edit" element={<GoodsPickupOrderFormView />} />
          <Route path="goods-delivery-orders" element={<GoodsDeliveryOrderListView />} />
          <Route path="goods-delivery-orders/new" element={<GoodsDeliveryOrderFormView />} />
          <Route path="goods-delivery-orders/:id" element={<GoodsDeliveryOrderDetailView />} />
          <Route path="goods-delivery-orders/:id/edit" element={<GoodsDeliveryOrderFormView />} />

          {/* Financeiro */}
          <Route path="financial/transactions" element={<FinancialTransactionsView />} />
          <Route path="financial/credit-cards" element={<CreditCardsView />} />
          <Route path="financial/recurring" element={<RecurringTransactionsView />} />
          <Route path="financial/imports" element={<TransactionImportView />} />
          <Route path="financial/import-correlations" element={<ImportCorrelationsView />} />
          <Route path="financial/accounting-plans" element={<AccountingPlanView />} />
          <Route path="financial/product-earn" element={<ProductEarnView />} />

          {/* Relatórios */}
          <Route path="reports/sales" element={<SalesReportView />} />
          <Route path="reports/stock" element={<StockReportsView />} />
          <Route path="reports/stock-movements" element={<StockReportsView />} />
          <Route path="reports/accounting" element={<AccountingReportsView />} />
          <Route path="reports/financial" element={<FinancialReportsView />} />
          <Route path="reports/shipping" element={<ShippingReportsPage />} />
          <Route path="reports/purchases" element={<PurchaseReportView />} />

          {/* Configuração - Fiscal */}
          <Route path="settings/fiscal/invoice-transaction-types" element={<InvoiceTransactionTypeListView />} />
          <Route path="settings/fiscal/invoice-transaction-types/new" element={<InvoiceTransactionTypeFormView />} />
          <Route path="settings/fiscal/invoice-transaction-types/:id/edit" element={<InvoiceTransactionTypeFormView />} />

          {/* Configuração - Geral */}
          <Route path="settings/general/holidays" element={<HolidayListView />} />
          <Route path="settings/general/holidays/new" element={<HolidayFormView />} />
          <Route path="settings/general/holidays/:id/edit" element={<HolidayFormView />} />
          <Route path="settings/fiscal/companies" element={<CompanyListView />} />
          <Route path="settings/fiscal/companies/new" element={<CompanyFormView />} />
          <Route path="settings/fiscal/companies/:id/edit" element={<CompanyFormView />} />
          <Route path="settings/fiscal/invoice-invalidations" element={<InvoiceInvalidationListView />} />
          <Route path="settings/fiscal/invoice-invalidations/new" element={<InvoiceInvalidationFormView />} />
          <Route path="settings/fiscal/invoice-invalidations/:id" element={<InvoiceInvalidationDetailView />} />
          <Route path="settings/fiscal/sefaz-communication-logs" element={<SefazCommunicationLogListView />} />
          <Route path="settings/fiscal/sefaz-communication-logs/:id" element={<SefazCommunicationLogDetailView />} />

          {/* Configuração - Operações */}
          <Route path="settings/operations/processes" element={<ProcessExecutionView />} />

          {/* Configuração - Vendas */}
          <Route path="settings/sales/order-types" element={<OrderTypeListView />} />
          <Route path="settings/sales/order-types/new" element={<OrderTypeFormView />} />
          <Route path="settings/sales/order-types/:id/edit" element={<OrderTypeFormView />} />
          <Route path="settings/sales/order-statuses" element={<OrderStatusListView />} />
          <Route path="settings/sales/order-statuses/new" element={<OrderStatusFormView />} />
          <Route path="settings/sales/order-statuses/:id/edit" element={<OrderStatusFormView />} />
          <Route path="settings/sales/sales-channels" element={<SalesChannelListView />} />
          <Route path="settings/sales/sales-channels/new" element={<SalesChannelFormView />} />
          <Route path="settings/sales/sales-channels/:id/edit" element={<SalesChannelFormView />} />
          <Route path="settings/sales/sales-goals" element={<SalesGoalsView />} />

          {/* Configuração - Transporte */}
          <Route path="settings/transport/drivers" element={<DriverListView />} />
          <Route path="settings/transport/drivers/new" element={<DriverFormView />} />
          <Route path="settings/transport/drivers/:id/edit" element={<DriverFormView />} />
          <Route path="settings/transport/vehicles" element={<VehicleListView />} />
          <Route path="settings/transport/vehicles/new" element={<VehicleFormView />} />
          <Route path="settings/transport/vehicles/:id/edit" element={<VehicleFormView />} />
          <Route path="settings/transport/trackers" element={<TrackersView />} />
          <Route path="settings/transport/shipping-providers" element={<ShippingProviderListView />} />

          {/* Configuração - Financeiro */}
          <Route path="settings/financial/variable-fees" element={<VariableFeesView />} />
          <Route path="settings/financial/receiving-hub" element={<ReceivingHubView />} />
          <Route path="settings/financial/checking-accounts" element={<CheckingAccountListView />} />
          <Route path="settings/financial/checking-accounts/new" element={<CheckingAccountFormView />} />
          <Route path="settings/financial/checking-accounts/:id/edit" element={<CheckingAccountFormView />} />
          <Route path="settings/financial/credit-cards" element={<CreditCardListView />} />
          <Route path="settings/financial/credit-cards/new" element={<CreditCardFormView />} />
          <Route path="settings/financial/credit-cards/:id/edit" element={<CreditCardFormView />} />

          {/* Rotas de Motorista */}
          <Route path="shipping-orders" element={<InvoiceShippingOrderListView />} />
          <Route path="driver-routes" element={<DriverRouteListView />} />
          <Route path="driver-routes/new" element={<DriverRouteFormView />} />
          <Route path="driver-routes/:id/edit" element={<DriverRouteFormView />} />
          <Route path="driver-routes/:id" element={<DriverRouteDetailView />} />
          <Route path="fleet-map" element={<FleetMapView />} />

          {/* Documentação: manual do usuário por perfil (a API checa user_guide.<perfil>) */}
          <Route path="documentation" element={<UserGuidePage />} />

          {/* Módulo Eventos: eventos, convites (Pedidos de Entrega) e planejamento de rota */}
          <Route path="events" element={<EventListView />} />
          <Route path="events/new" element={<EventFormView />} />
          <Route path="events/:id/edit" element={<EventFormView />} />
          <Route path="events/invitations" element={<InvitationListView />} />
          <Route path="events/invitations/new" element={<InvitationFormView />} />
          <Route path="events/invitations/:id" element={<InvitationDetailView />} />
          <Route path="events/route-planning" element={<RoutePlanningView />} />

          {/* Entregas */}
          <Route path="delivery" element={<DeliveryExecutionListView />} />
          <Route path="delivery/:id" element={<DeliveryExecutionDetailView />} />
        </Route>

        {/* Redirects e rotas catch-all */}
        <Route path="/admin" element={<LegacyRedirect />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Router>
  );
};

// App wrapper com providers e configuração
export default function App() {
  console.log('🎯 APP.TSX: App component iniciando...');
  
  const [configLoaded, setConfigLoaded] = React.useState(false);
  const [configError, setConfigError] = React.useState<string | null>(null);

  // Carregar configuração da aplicação
  useEffect(() => {
    console.log('⚙️ APP.TSX: useEffect de configuração executando...');
    
    const initializeApp = async () => {
      try {
        console.log('🔧 APP.TSX: Carregando configuração...');
        await loadConfig();
        console.log('✅ APP.TSX: Configuração carregada com sucesso');
        setConfigLoaded(true);
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Erro desconhecido';
        console.error('❌ APP.TSX: Falha ao carregar configurações:', message);
        setConfigError(`Falha ao carregar configurações: ${message}`);
      }
    };

    initializeApp();
  }, []);

  console.log('📊 APP.TSX: Estado atual:', { configLoaded, configError });

  // Mostrar loading enquanto carrega configuração
  if (!configLoaded && !configError) {
    console.log('⏳ APP.TSX: Mostrando AppLoading...');
    return <AppLoading />;
  }

  // Mostrar erro se falha ao carregar configuração
  if (configError) {
    console.log('🚫 APP.TSX: Mostrando AppError:', configError);
    return <AppError error={configError} />;
  }

  console.log('🎉 APP.TSX: Renderizando app principal com AuthProvider...');
  // App principal com providers
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}